import React, { useEffect, useState, useMemo } from 'react';

import { Switch } from 'antd';
import Axios from 'axios';

import styled, { css } from 'styled-components';

import { getMoment, getStringDatesInRange, toISODate } from '@libs/NwMoment';

import { useLayoutContext } from '@contexts/LayoutContext';

import {
    useContactsSelector,
    useContactsSelectorDispatch,
} from '@components/ContactsSelector/Common/ContactsSelectorContext';
import ModelsAvailabilityForm from '@components/ContactsSelector/ModelsSelector/ModelsAvailability/ModelsAvailabilityForm';
import MiniLoading from '@components/Gui/MiniLoading';
import { NwLinkButton } from '@components/Gui/NwButton';
import NwIcon from '@components/Gui/NwIcon';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

// Shared error message styles
const errorMessageStyles = (textAlign = 'right') => css`
    p {
        color: ${(props) => props.theme.DangerColor};
        font-size: ${(props) => props.theme.FontSizeSm};
        font-weight: normal;
        margin: 0.5rem 0 0 0;
        padding: 0;
        text-align: ${textAlign};
        line-height: 1.5em;
    }
`;

const AvailabilityCheckWrapper = styled.div`
    .first-row {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 0.5rem;
        margin-bottom: 0.5rem;
    }
    .second-row {
        margin-top: 1.5rem;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        justify-content: flex-end;
        gap: 0.5rem;
    }
    .third-row {
        ${errorMessageStyles()}
    }
`;

const AvailabilityCheckWrapperMobile = styled.div`
    .trigger {
        padding: 0.75rem 0 0.25rem 0;
        text-align: center;
    }
    .options {
        position: relative;
        background: #fff;
        border-radius: 6px;
        border: 1px solid #f0f0f0;
        display: flex;
        padding: 1em 2em;
        min-height: 4rem;

        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        justify-content: center;
        align-items: center;

        .close-button {
            position: absolute;
            top: 0.25rem;
            right: 0.75rem;
        }
        .error-message {
            ${errorMessageStyles('center')}
        }
    }
`;

const ModelsAvailability = ({ models, switchSize = 'medium' }) => {
    const [visible, setVisible] = useState(false);
    const [checkDates, setCheckDates] = useState([]);
    const [availabilityRange, setAvailabilityRange] = useState('Day');

    const { actions, context, cache } = useContactsSelector();
    const { modelAvailability } = cache;

    const layoutContext = useLayoutContext();
    const { isMobile } = layoutContext;

    const { currentEvent } = context;
    const { isFetchingAvailability } = actions;

    const cs_dispatch = useContactsSelectorDispatch();

    const currentEventDates = useMemo(() => {
        if (currentEvent) {
            if (currentEvent.EventDates) {
                const sortedDates = currentEvent.EventDates.sort(
                    (a, b) => getMoment(a.FromDate) - getMoment(b.FromDate)
                );
                return sortedDates.map((ed) => toISODate(ed.FromDate));
            }
        }
        return [];
    }, [currentEvent]);

    useEffect(() => {
        if (currentEvent) {
            if (currentEvent.EventDates) {
                var paramDate = [...currentEventDates];
                setAvailabilityRange('Dates');
                setCheckDates(paramDate);
                setVisible(true);
            } else if (currentEvent.StartDate && currentEvent.EndDate) {
                var paramDates = getStringDatesInRange(currentEvent.StartDate, currentEvent.EndDate);
                setAvailabilityRange(paramDates.length > 1 ? 'Period' : 'Day');
                setCheckDates(paramDates);
                setVisible(true);
            }
        }
    }, []);

    const datesChanged = useMemo(() => {
        //I need to check if checkDates are different from modelAvailability.dates
        if (modelAvailability && modelAvailability.dates && checkDates && checkDates.length > 0) {
            const modelAvailabilityDatesSet = new Set(modelAvailability.dates);
            const checkDatesSet = new Set(checkDates);
            return (
                checkDates.some((date) => !modelAvailabilityDatesSet.has(date)) ||
                modelAvailability.dates.some((date) => !checkDatesSet.has(date))
            );
        }
        return true;
    }, [checkDates, modelAvailability]);

    const eventDatesAreIncludedInCheckDates = useMemo(() => {
        if (currentEventDates && currentEventDates.length > 0 && checkDates && checkDates.length > 0) {
            // Normalize dates to YYYY-MM-DD format (first 10 characters) to ignore time parts
            const checkDatesSet = new Set(checkDates.map((date) => date.substring(0, 10)));
            const normalizedEventDates = currentEventDates.map((date) => date.substring(0, 10));
            return normalizedEventDates.every((date) => checkDatesSet.has(date));
        }
        return false;
    }, [checkDates, currentEventDates]);

    const loadAvailability = () => {
        if (isFetchingAvailability) {
            return;
        }
        if (!visible) {
            cs_dispatch({
                type: 'SetIsFetchingAvailability',
                value: false,
            });
            cs_dispatch({
                type: 'UpdateModelAvailability',
                data: null,
            });
            return;
        }
        if (checkDates && checkDates.length > 0) {
            const modelIds = models.map((model) => model.ID);
            if (modelIds.length > 0) {
                const modelAvailIDs = modelAvailability ? modelAvailability.models.map((model) => model.ID) : [];
                const hasNewModels = modelIds.some((modelID) => !modelAvailIDs.includes(modelID));
                if (hasNewModels || datesChanged) {
                    cs_dispatch({
                        type: 'SetIsFetchingAvailability',
                        value: true,
                    });
                    const newValues = {
                        dates: checkDates,
                        models: modelIds,
                    };
                    if (currentEvent?.ID) {
                        newValues.eventid = currentEvent.ID;
                    }
                    if (availabilityRange === 'Day') {
                        newValues.isSingleDay = true;
                    }
                    Axios.post('/calendar/checkmodelsavailability', newValues)
                        .then((response) => {
                            if (Array.isArray(response)) {
                                const eventsData = {
                                    result: [...response],
                                    dates: checkDates,
                                    isSingleDay: availabilityRange === 'Day',
                                    eventDatesIncluded: eventDatesAreIncludedInCheckDates,
                                    enabled: visible,
                                    models: models,
                                };
                                cs_dispatch({
                                    type: 'UpdateModelAvailability',
                                    data: eventsData,
                                });
                            } else {
                                console.error('response is not an array', response);
                            }
                        })
                        .catch((error) => {
                            console.error('error', error);
                        })
                        .finally(() => {
                            cs_dispatch({
                                type: 'SetIsFetchingAvailability',
                                value: false,
                            });
                        });
                }
            }
        }
    };

    useEffect(() => {
        loadAvailability();
    }, [checkDates, availabilityRange, visible, models]);

    const handleUpdateAvailability = (range, dates) => {
        let newDates = [];
        switch (range) {
            case 'Period':
                newDates = getStringDatesInRange(dates[0], dates[1]);
                break;
            case 'Dates':
                newDates = currentEventDates;
                break;
            default:
                newDates = dates;
                break;
        }
        setAvailabilityRange(range);
        setCheckDates(newDates);
    };

    if (isMobile) {
        return (
            <AvailabilityCheckWrapperMobile>
                <div className="trigger">
                    {!visible && (
                        <NwLinkButton
                            icon={byPrefixAndName.fasl['calendar']}
                            onClick={() => setVisible(true)}
                            label="check availability"
                        />
                    )}
                </div>
                {visible && (
                    <>
                        <div className="options">
                            <div className="close-button">
                                <NwIcon
                                    pointer
                                    onClick={() => setVisible(false)}
                                    icon={byPrefixAndName.fasl['times']}
                                />
                            </div>
                            <ModelsAvailabilityForm
                                availabilityRange={availabilityRange}
                                checkDates={checkDates}
                                eventDates={currentEventDates}
                                onUpdate={handleUpdateAvailability}
                                isFetchingAvailability={isFetchingAvailability}
                            />
                            {modelAvailability &&
                                currentEventDates &&
                                currentEventDates.length > 0 &&
                                !modelAvailability.eventDatesIncluded && (
                                    <div className="error-message">
                                        <p>The requested availability period does not include the event dates.</p>
                                    </div>
                                )}
                        </div>
                    </>
                )}
            </AvailabilityCheckWrapperMobile>
        );
    }

    return (
        <AvailabilityCheckWrapper>
            <div className="first-row">
                <Switch checked={visible} onChange={setVisible} size={switchSize} />
                <span className="label">check availability</span>
                {isFetchingAvailability && <MiniLoading />}
            </div>
            {visible && (
                <>
                    <div className="second-row" style={{ opacity: isFetchingAvailability ? 0.4 : 1 }}>
                        <ModelsAvailabilityForm
                            availabilityRange={availabilityRange}
                            checkDates={checkDates}
                            eventDates={currentEventDates}
                            onUpdate={handleUpdateAvailability}
                            isFetchingAvailability={isFetchingAvailability}
                        />
                    </div>
                    {modelAvailability &&
                        currentEventDates &&
                        currentEventDates.length > 0 &&
                        !modelAvailability.eventDatesIncluded && (
                            <div className="third-row">
                                <p>The requested availability period does not include the event dates.</p>
                            </div>
                        )}
                </>
            )}
        </AvailabilityCheckWrapper>
    );
};

export default ModelsAvailability;
